import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { useUpdateStore } from '../hooks/use-store-mutations';
import { useStoreDetail } from '../hooks/use-stores';
import {
  emailSenderSchema,
  fromEmailSettings,
  toEmailSettingsPatch,
  type EmailSenderValues,
} from '../schemas/email-sender-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['fromName', 'replyTo']);
const mapField = (field: string) => field.replace(/^settings\.email\./, '');

export interface EmailSenderCardProps {
  store: Store;
  readOnly?: boolean;
}

/** Sender name and reply-to for the store's shopper emails; replies otherwise land at the platform. */
export function EmailSenderCard({ store, readOnly = false }: EmailSenderCardProps) {
  const { t } = useTranslation(['stores', 'common']);
  const message = useErrorMessage();
  const update = useUpdateStore(store.id, 'stores:emailSender.saved');
  const form = useForm<EmailSenderValues>({
    resolver: useI18nResolver(emailSenderSchema),
    defaultValues: fromEmailSettings(store),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toEmailSettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <FormSection
      id={SECTION_ANCHORS.emailSender}
      tour="settings.notifications.sender"
      title={t('stores:emailSender.title')}
      description={t('stores:emailSender.description')}
    >
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('stores:emailSender.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <fieldset disabled={readOnly} className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="fromName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stores:emailSender.senderName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={store.name} maxLength={120} {...field} />
                  </FormControl>
                  <FormDescription>{t('stores:emailSender.senderNameHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="replyTo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stores:emailSender.replyTo')}</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={store.supportEmail ?? ''} {...field} />
                  </FormControl>
                  <FormDescription>{t('stores:emailSender.replyToHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </fieldset>
          {readOnly ? null : (
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={update.isPending || !form.formState.isDirty}
              >
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}

/**
 * The card for one store, loaded on its own so the Notifications page can mount it without
 * knowing the store record. Saving needs settings.write and an open store.
 */
export function EmailSenderSection({ storeId }: { storeId: string }) {
  const detail = useStoreDetail(storeId);
  const { editable } = useStoreEditable({ store: detail.data, permission: 'settings.write' });
  if (!detail.data) return <Skeleton className="h-40" />;
  return <EmailSenderCard key={detail.data.updatedAt} store={detail.data} readOnly={!editable} />;
}
